import { TitleCasePipe, NgFor, AsyncPipe } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import {
	ButtonComponent,
	SlottedModalComponent,
	ButtonState,
	HistoryService,
	RegEx,
	RoutedModalBaseComponent,
	ShopperService,
} from '@woolworthsnz/styleguide';
import { Observable, Subject } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';
import { PhoneNumber } from '../PhoneNumber';
import { OrderService, OrderState } from '@woolworthsnz/shop';
import { InvalidTypeDirective, InputErrorComponent, InputComponent, FormComponent } from '@woolworthsnz/form';

interface ErrorResponse {
	field: string;
	message: string;
}

@Component({
	selector: 'wnz-change-contact-details-modal',
	templateUrl: './change-contact-details-modal.component.html',
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		SlottedModalComponent,
		FormsModule,
		FormComponent,
		ReactiveFormsModule,
		NgFor,
		InputComponent,
		InputErrorComponent,
		InvalidTypeDirective,
		ButtonComponent,
		AsyncPipe,
	],
})
export class ChangeContactDetailsModalComponent extends RoutedModalBaseComponent implements OnInit, OnDestroy {
	phoneNumbers$: Observable<PhoneNumber[]>;
	phoneNumbers: FormGroup;
	buttonState: ButtonState;
	private destroyed$: Subject<boolean> = new Subject();

	constructor(
		protected router: Router,
		protected historyService: HistoryService,
		private shopperService: ShopperService,
		private orderService: OrderService,
		private titleCase: TitleCasePipe,
		private formBuilder: FormBuilder,
		private cdr: ChangeDetectorRef,
		protected activatedRoute: ActivatedRoute
	) {
		super(router, activatedRoute, historyService);
	}

	ngOnDestroy(): void {
		this.destroyed$.next(true);
		this.destroyed$.complete();
	}

	ngOnInit(): void {
		this.phoneNumbers = this.formBuilder.group(
			{
				primaryPhone: ['', [Validators.required, Validators.pattern(RegEx.PHONE)]],
				secondaryPhone: ['', Validators.pattern(RegEx.PHONE)],
			},
			{ updateOn: 'blur' }
		);

		this.phoneNumbers.controls['secondaryPhone'].valueChanges
			.pipe(
				takeUntil(this.destroyed$),
				tap((val) => {
					if (val === '') {
						this.phoneNumbers.controls['secondaryPhone'].markAsPristine();
					}
				})
			)
			.subscribe();

		this.phoneNumbers$ = this.orderService.state$.pipe(
			map((resp: OrderState) =>
				['primaryPhone', 'secondaryPhone'].map((label) => {
					const phoneNumber = <PhoneNumber>{};
					phoneNumber.label = this.titleCase.transform(label.replace('Phone', ''));
					if (resp.order?.contactNumbers) {
						phoneNumber.number =
							resp.order.contactNumbers[label as 'primaryPhone' | 'secondaryPhone'] || '';
					}
					return phoneNumber;
				})
			),
			tap((r: Array<PhoneNumber>) => {
				r.forEach((p) => this.phoneNumbers.controls[this.getControlName(p.label)].setValue(p.number || ''));
			})
		);
	}

	onSubmit(): void {
		this.buttonState = ButtonState.busy;

		this.shopperService
			.updatePhoneNumbers(this.phoneNumbers.value)
			.pipe(takeUntil(this.destroyed$))
			.subscribe(
				() => {
					this.buttonState = ButtonState.completed;
					this.close();
				},
				(err) => {
					this.buttonState = ButtonState.errored;
					this.phoneNumbers.setErrors({ serverError: true });
					// iterate err response and mark each control as errored
					err.error?.errors?.forEach((error: ErrorResponse) =>
						this.phoneNumbers.controls[this.getControlNameFromServerFieldName(error.field)]?.setErrors({
							serverError: true,
						})
					);
					this.cdr.markForCheck();
				}
			);
	}

	getControlName(label: string): string {
		return `${label.toLowerCase()}Phone`;
	}
	getControlNameFromServerFieldName(fieldName: string): string {
		return fieldName.charAt(0).toLowerCase() + fieldName.slice(1);
	}
}
